import { useQuery } from '@tanstack/react-query';
import { useUserStore } from '@/store/userStore';
import { getPopularClasses } from '@/services/edgeFunctionWrappers/dashboardStatsWrapper';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, Medal, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DateRange } from '@/utils/dashboardDateUtils';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

const MEDAL_COLORS = [
  'text-yellow-500',
  'text-gray-400',
  'text-amber-700',
];

export function TopCoursesWidget({ range }: { range: DateRange }) {
  const { gymId, user } = useUserStore();
  const { t } = useTranslation('dashboard');
  
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['top-courses', user?.id, gymId, range.startDate, range.endDate],
    queryFn: () => {
      if (!gymId) throw new Error('No active gym');
      return getPopularClasses(gymId,range.startDate,range.endDate);
    },
    enabled: !!gymId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            Top 3 cours
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-24">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const topCourses = data?.slice(0, 3) || [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Trophy className="h-4 w-4 text-primary" />
            Top 3 cours
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isError ? <div className="flex flex-col items-center gap-3 py-4"><p className="text-sm text-muted-foreground">{t('states.loadError')}</p><Button variant="outline" onClick={() => void refetch()}>{t('states.retry')}</Button></div> : topCourses.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Aucune donnée disponible
          </p>
        ) : (
          <ul className="space-y-2">
            {topCourses.map((course, index) => (
              <li 
                key={course.class_name} 
                className="flex items-center justify-between py-1.5 border-b last:border-0"
              >
                <div className="flex items-center gap-2">
                  <Medal className={cn("h-4 w-4", MEDAL_COLORS[index])} />
                  <span className="text-sm font-medium">{course.class_name}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {course.booking_count} réservations
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
